import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Tractor, CheckCircle2, AlertTriangle } from "lucide-react";

const SPECIES = ["Cattle", "Poultry", "Goat", "Sheep", "Pig", "Fish", "Rabbit", "Other"];
const VISIT_TYPES = ["Consultation", "Vaccination", "Fumigation", "Farm Setup", "Deworming", "Other"];

export default function FarmVisit() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    owner_name: "", owner_phone: "", owner_email: "",
    farm_name: "", address: "", latitude: null, longitude: null,
    species: [], species_other: "",
    herd_size: 1, visit_type: "Consultation", urgency: "Routine",
    preferred_date: "", notes: "", confirmed_accurate: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [locating, setLocating] = useState(false);

  const toggleSpecies = (s) => {
    setForm((f) => ({ ...f, species: f.species.includes(s) ? f.species.filter((x) => x !== s) : [...f.species, s] }));
  };

  const pinLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported by this device.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        toast.success("Location pinned to the farm.");
        setLocating(false);
      },
      (err) => { toast.error("Could not get location: " + err.message); setLocating(false); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.confirmed_accurate) return toast.error("Please confirm the information is accurate.");
    if (form.species.length === 0) return toast.error("Select at least one species.");
    setBusy(true);
    try {
      await api.post("/farm-visits", { ...form, herd_size: parseInt(form.herd_size || 0) });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen ankara" data-testid="farm-thank-you">
        <Header />
        <div className="max-w-xl mx-auto px-4 py-24 text-center">
          <div className="h-16 w-16 rounded-full bg-[#0F766E]/15 flex items-center justify-center mx-auto"><CheckCircle2 size={32} className="text-[#0F766E]" /></div>
          <h1 className="font-display text-3xl mt-6">Farm visit request received</h1>
          <p className="mt-4 text-[#57534E]">Thank you. Jay's Veterinary Consult and Services will review your farm details and contact you with a quote using the phone number provided.</p>
          <a href="/" className="mt-8 inline-block btn-primary px-6 py-3 rounded-full text-sm font-semibold">Back to home</a>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen ankara" data-testid="farm-visit-page">
      <Header />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-24">
        <div className="chip chip-terra mb-3">Farm visit</div>
        <h1 className="font-display text-3xl md:text-5xl leading-[1.05]">Farm Visit Booking</h1>
        <p className="mt-4 text-[#57534E]">Livestock consult, vaccination, fumigation or farm setup — we'll come to you. Share your GPS pin and herd details and we'll quote your on-site job.</p>

        <div className="mt-6 flex gap-3 p-4 rounded-2xl bg-[#FFF1E0] border-2 border-[#C2410C]/40">
          <AlertTriangle size={20} className="text-[#C2410C] shrink-0 mt-0.5" />
          <p className="text-sm text-[#7C2D12]">Home/farm visits are subject to distance and availability. For an emergency, seek immediate on-site veterinary care in your area.</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-8" data-testid="farm-form">
          <Section title="Farmer / Owner">
            <Field label="Owner's Name" required>
              <input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="f-owner-name" className={INP} />
            </Field>
            <Field label="Phone Number" required>
              <input required type="tel" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} data-testid="f-owner-phone" className={INP} />
            </Field>
            <Field label="Email (optional)">
              <input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="f-owner-email" className={INP} />
            </Field>
          </Section>

          <Section title="Farm Location">
            <Field label="Farm Name (optional)">
              <input value={form.farm_name} onChange={(e) => setForm({ ...form, farm_name: e.target.value })} data-testid="f-farm-name" className={INP} />
            </Field>
            <Field label="Address / Landmark" required>
              <textarea required rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="f-address" className={INP} />
            </Field>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#57534E]">GPS pin (recommended)</label>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button type="button" onClick={pinLocation} disabled={locating}
                  className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold disabled:opacity-60"
                  data-testid="f-pin-btn">
                  <MapPin size={14} /> {locating ? "Pinning…" : (form.latitude ? "Update pin" : "Pin my farm location")}
                </button>
                {form.latitude != null && (
                  <a href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0F766E] underline" data-testid="f-map-link">
                    View on Google Maps ({form.latitude.toFixed(5)}, {form.longitude.toFixed(5)})
                  </a>
                )}
              </div>
            </div>
          </Section>

          <Section title="Livestock Details">
            <Field label="Species (choose all that apply)" required>
              <div className="flex flex-wrap gap-2">
                {SPECIES.map((s) => (
                  <button type="button" key={s} onClick={() => toggleSpecies(s)} data-testid={`f-species-${s}`}
                    className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${form.species.includes(s) ? "bg-[#0F766E] text-white border-[#0F766E]" : "bg-white text-[#1C1917] border-[#EAD9B0] hover:border-[#0F766E]"}`}>{s}</button>
                ))}
              </div>
            </Field>
            {form.species.includes("Other") && (
              <Field label="Specify other species">
                <input value={form.species_other} onChange={(e) => setForm({ ...form, species_other: e.target.value })} data-testid="f-species-other" className={INP} />
              </Field>
            )}
            <Field label="Herd size (total animals)" required>
              <input required type="number" min="1" value={form.herd_size} onChange={(e) => setForm({ ...form, herd_size: e.target.value })} data-testid="f-herd" className={INP} />
            </Field>
          </Section>

          <Section title="Visit">
            <Field label="Visit type" required>
              <select value={form.visit_type} onChange={(e) => setForm({ ...form, visit_type: e.target.value })} data-testid="f-visit-type" className={INP}>
                {VISIT_TYPES.map((v) => <option key={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Urgency" required>
              <div className="flex flex-wrap gap-2">
                {["Routine", "Within a week", "Urgent (48h)"].map((u) => (
                  <button type="button" key={u} onClick={() => setForm({ ...form, urgency: u })} data-testid={`f-urg-${u}`}
                    className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${form.urgency === u ? "bg-[#0F766E] text-white border-[#0F766E]" : "bg-white border-[#EAD9B0] hover:border-[#0F766E]"}`}>{u}</button>
                ))}
              </div>
            </Field>
            <Field label="Preferred date (optional)">
              <input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="f-date" className={INP} />
            </Field>
            <Field label="Additional notes (optional)">
              <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="f-notes" className={INP} />
            </Field>
          </Section>

          <label className="flex items-start gap-3 p-4 bg-white border border-[#EAD9B0] rounded-2xl cursor-pointer">
            <input type="checkbox" checked={form.confirmed_accurate} onChange={(e) => setForm({ ...form, confirmed_accurate: e.target.checked })} data-testid="f-confirm" className="mt-1 h-4 w-4 accent-[#0F766E]" />
            <span className="text-sm">I confirm the farm details are accurate and Dr. Juwon may contact me for scheduling and quotation.</span>
          </label>

          <button type="submit" disabled={busy || !form.confirmed_accurate}
            className="btn-primary w-full py-4 rounded-full text-base font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="f-submit">
            <Tractor size={16} /> {busy ? "Submitting…" : "Request Farm Visit"}
          </button>
        </form>
      </section>
      <Footer />
    </div>
  );
}

const INP = "w-full rounded-lg border border-[#EAD9B0] bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]";

function Section({ title, children }) {
  return (
    <div className="bg-white/60 border border-[#EAD9B0] rounded-3xl p-6">
      <h2 className="font-display text-xl mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#57534E]">{label}{required && <span className="text-[#C2410C]">*</span>}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
